/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Collapse non-trading gaps in a sorted array of candle bars.
 * For intraday data, detects session breaks (gaps > 3× normal interval)
 * and re-indexes timestamps so bars are evenly spaced at exactly one
 * interval. The original timestamp is preserved as `time_real` so cursors
 * and tooltips can still reference the real wall-clock time.
 *
 * @param {Array} bars  Sorted array of {time, open, high, low, close, ...}
 * @param {string} tf   Timeframe code like 'M15', 'H1', 'D1'
 * @returns {Array}     New array with compressed timestamps
 */
export const TF_SECONDS = { S1:1, M1:60, M5:300, M15:900, M30:1800, H1:3600, H4:14400, D1:86400, W1:604800 };

// Return one finite, latest-wins candle for each real timestamp.
// This is the canonical input for both price and indicator alignment.
export function canonicalizeBars(bars) {
    if (!Array.isArray(bars)) return [];
    const byRealTime = new Map();
    bars.forEach((bar) => {
        if (!bar || typeof bar !== 'object') return;
        const time = Number(bar.time);
        const realTime = Number(bar.time_real ?? bar.time);
        const open = Number(bar.open);
        const high = Number(bar.high);
        const low = Number(bar.low);
        const close = Number(bar.close);
        if (![time, realTime, open, high, low, close].every(Number.isFinite)) return;
        byRealTime.set(realTime, {
            ...bar,
            time,
            ...(bar.time_real != null ? { time_real: realTime } : {}),
            open,
            high,
            low,
            close,
        });
    });
    return [...byRealTime.values()].sort((a, b) => a.time - b.time);
}

export function compressGaps(bars, tf) {
    const source = canonicalizeBars(bars);
    if (source.length < 2) return source;
    const upper = (tf || '').toUpperCase();
    // Only compress intraday timeframes (sub-daily)
    if (['D1', 'W1', 'MN', 'MN1'].includes(upper)) return source;
    const interval = TF_SECONDS[upper] || 300;
    const gapThreshold = interval * 3;
    const result = [];
    let cumOffset = 0;
    for (let i = 0; i < source.length; i++) {
        const t = source[i].time;
        if (typeof t !== 'number') { result.push(source[i]); continue; }
        if (i > 0) {
            const prevT = source[i - 1].time;
            const gap = t - prevT;
            if (gap > gapThreshold) cumOffset += gap - interval;
        }
        result.push({ ...source[i], time: t - cumOffset, time_real: t });
    }
    return result;
}
